import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "../../styles/addReceipt.module.css";
import { formatCurrency } from "../../../../../shared/utils/formatCurrency";
import apiFetch from "../../../../../shared/services/apiClient";
import { useTipoCambio } from "../../../services/useTipoCambio";
import { useMultimoneda } from "../../../../../shared/hooks/useMultimoneda";

/** Mensaje de error inline bajo un campo */
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        color: "#f87171",
        fontSize: "0.77rem",
        marginTop: 4,
        fontWeight: 500,
      }}
    >
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize: "0.72rem" }} />
      {msg}
    </div>
  );
}

const BADGE = {
  BCCR:     { color: "#4ade80", text: "BCCR" },
  MANUAL:   { color: "#fb923c", text: "Manual" },
  FALLBACK: { color: "#f87171", text: "Sin TC — ingresalo" },
};

export function ReceiptDetails({
  receiptData,
  updateField,
  selectedFilial,
  setSelectedFilial,
  onDistribute,
  displayValue,
  setDisplayValue,
  readOnly = false,
  fechaPagoEditable = false,
  filialEditable = false,
  errors = {},
  clearFilialError,
  soloSaldoFavor = false,
  referenciaAutoSaldo = null,
  // bank currency tracking
  setBancoMoneda,
}) {
  return (
    <div className={styles["receipt-details"]}>
      <ReceiptDetailsHeader />
      <ReceiptDetailsBody
        receiptData={receiptData}
        updateField={updateField}
        selectedFilial={selectedFilial}
        setSelectedFilial={setSelectedFilial}
        onDistributeAllMoney={onDistribute}
        displayValue={displayValue}
        setDisplayValue={setDisplayValue}
        readOnly={readOnly}
        fechaPagoEditable={fechaPagoEditable}
        filialEditable={filialEditable}
        errors={errors}
        clearFilialError={clearFilialError}
        soloSaldoFavor={soloSaldoFavor}
        referenciaAutoSaldo={referenciaAutoSaldo}
        setBancoMoneda={setBancoMoneda}
      />
    </div>
  );
}

function ReceiptDetailsHeader() {
  const { t } = useTranslation();
  return (
    <div className={styles["receipt-details-header"]}>
      <h1>
        <i className={`fa-solid fa-money-bill ${styles["icon-receipt-details"]}`} />{" "}
        {t("invoices.detallesRecibo")}
      </h1>
    </div>
  );
}

function ReceiptDetailsBody({
  receiptData,
  updateField,
  selectedFilial,
  setSelectedFilial,
  onDistributeAllMoney,
  displayValue,
  setDisplayValue,
  readOnly,
  fechaPagoEditable = false,
  errors,
  clearFilialError,
  soloSaldoFavor = false,
  referenciaAutoSaldo = null,
  filialEditable,
  setBancoMoneda,
}) {
  const { t } = useTranslation();
  const multimoneda = useMultimoneda();
  const [filiales, setFiliales]               = useState([]);
  const [cuentaBancarias, setCuentaBancarias] = useState([]);
  const { tipoCambio, loading: loadingTC }    = useTipoCambio();

  // Detect selected bank's moneda
  const bancoSeleccionado = cuentaBancarias.find(
    (c) => c.id === Number(receiptData.cuenta_bancaria)
  );
  // Si multimoneda está apagada, siempre tratamos el banco como CRC
  const bancoMoneda = multimoneda ? (bancoSeleccionado?.moneda || "CRC") : "CRC";
  const monedaSimbolo = bancoMoneda === "USD" ? "$" : "₡";

  // When bank changes → update moneda_pago from bank's moneda
  useEffect(() => {
    if (readOnly || !bancoSeleccionado) return;
    const monedaEfectiva = multimoneda ? bancoMoneda : "CRC";
    updateField("moneda_pago", monedaEfectiva);
    if (setBancoMoneda) setBancoMoneda(monedaEfectiva);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptData.cuenta_bancaria, bancoMoneda, multimoneda]);

  // Cargar TC_compra desde BCCR solo si multimoneda está activa
  useEffect(() => {
    if (readOnly || !multimoneda) return;
    if (tipoCambio?.tipo_compra) {
      updateField("tipo_cambio", Number(tipoCambio.tipo_compra));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCambio, multimoneda]);

  useEffect(() => {
    async function fetchFiliales() {
      try {
        const res  = await apiFetch("/api/residents/filial/");
        const data = await res.json();
        setFiliales(data.results ?? data ?? []);
      } catch {}
    }
    async function fetchCuentaBancaria() {
      try {
        const res  = await apiFetch("/api/finance/cuentaBancaria/");
        const data = await res.json();
        const todas = data.results ?? [];
        // Sin multimoneda → mostrar solo cuentas en CRC
        const filtradas = multimoneda
          ? todas
          : todas.filter((c) => (c.moneda || "CRC").toUpperCase() === "CRC");
        setCuentaBancarias(filtradas);
      } catch {}
    }
    fetchFiliales();
    fetchCuentaBancaria();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multimoneda]);

  // Auto-seleccionar primeros valores cuando NO es readOnly
  useEffect(() => {
    if (readOnly) return;
    if (filiales.length > 0 && selectedFilial === 0) {
      setSelectedFilial(Number(filiales[0].id));
    }
    if (cuentaBancarias.length > 0 && receiptData.cuenta_bancaria === 0) {
      updateField("cuenta_bancaria", cuentaBancarias[0].id);
    }
  }, [filiales, selectedFilial, cuentaBancarias, receiptData.cuenta_bancaria, readOnly]);

  const errorBorder = (field) =>
    errors[field] ? { borderColor: "#ef4444" } : undefined;

  const badge = BADGE[tipoCambio?.fuente] || BADGE.BCCR;

  return (
    <div className={styles["receipt-details-body"]}>
      {/* ── Fila 1: Fecha + Filial ───────────────────────────────── */}
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label">
              {t("invoices.fechaDePago")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              className="form-control"
              type="date"
              value={receiptData.fecha_pago}
              onChange={(e) => updateField("fecha_pago", e.target.value)}
              disabled={readOnly && !fechaPagoEditable}
              style={errorBorder("fecha_pago")}
            />
            <FieldError msg={errors.fecha_pago} />
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label className="form-label">
              {t("invoices.filial")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            {filiales.length === 0 ? (
              <div style={{
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 6, padding: "8px 12px", color: "#f87171",
                fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 7,
              }}>
                <i className="fa-solid fa-triangle-exclamation" />
                {t("invoices.sinFiliales")}
              </div>
            ) : (
              <>
                <select
                  className="form-select"
                  value={selectedFilial}
                  onChange={(e) => {
                    setSelectedFilial(Number(e.target.value));
                    if (clearFilialError) clearFilialError();
                  }}
                  disabled={readOnly && !filialEditable}
                  style={errorBorder("filial")}
                >
                  {filiales.map((fil) => (
                    <option value={fil.id} key={fil.id}>{fil.idFilial}</option>
                  ))}
                </select>
                <FieldError msg={errors.filial} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Fila 2: Cuenta bancaria (PRIMERO) ────────────────────── */}
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label">{t("invoices.medioPago")}</label>
            <select
              className="form-select"
              value={receiptData.metodo_pago}
              onChange={(e) => updateField("metodo_pago", e.target.value)}
              disabled={readOnly}
            >
              <option value="TRANSFERENCIA">{t("invoices.transferencia")}</option>
              <option value="EFECTIVO">{t("invoices.efectivo")}</option>
              <option value="CHEQUE">{t("invoices.cheque")}</option>
            </select>
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label className="form-label">
              {t("invoices.cuentaBancaria")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            {cuentaBancarias.length === 0 ? (
              <div style={{
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 6, padding: "8px 12px", color: "#f87171",
                fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 7,
              }}>
                <i className="fa-solid fa-triangle-exclamation" />
                {t("invoices.sinCuentasBancarias")}
              </div>
            ) : (
              <>
                <select
                  className="form-select"
                  value={receiptData.cuenta_bancaria}
                  onChange={(e) => updateField("cuenta_bancaria", Number(e.target.value))}
                  disabled={readOnly}
                  style={errorBorder("cuenta_bancaria")}
                >
                  {cuentaBancarias.map((cta) => (
                    <option value={cta.id} key={cta.id}>
                      {cta.banco} — {cta.numero_cuenta}
                      {multimoneda ? (cta.moneda === "USD" ? " (USD)" : " (₡)") : ""}
                    </option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_bancaria} />
                {/* Moneda de la cuenta — solo si es USD, como referencia rápida */}
                {multimoneda && bancoSeleccionado && bancoMoneda === "USD" && (
                  <div style={{ fontSize: "0.74rem", color: "#60a5fa", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
                    <i className="fa-solid fa-circle-info" style={{ fontSize: "0.65rem" }} />
                    Cuenta en dólares — TC compra se aplica al monto
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── TC Compra — card (solo multimoneda) ──────────────────── */}
      {!readOnly && multimoneda && (
        <div style={{
          marginTop: 8,
          border: "1px solid var(--border-card)",
          borderRadius: 10,
          overflow: "hidden",
          background: "rgba(255,255,255,0.02)",
        }}>
          {/* Cabecera */}
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "9px 14px",
            borderBottom: "1px solid var(--border-card)",
            background: bancoMoneda === "USD"
              ? "rgba(96,165,250,0.05)"
              : "rgba(249,115,22,0.04)",
          }}>
            <i className="fa-solid fa-arrow-right-arrow-left"
               style={{ color: bancoMoneda === "USD" ? "#60a5fa" : "#f97316", fontSize: "0.7rem" }} />
            <span style={{ fontSize: "0.67rem", fontWeight: 700, color: "var(--text-muted)",
              letterSpacing: "0.6px", textTransform: "uppercase" }}>
              Tipo de Cambio
            </span>
            {bancoMoneda === "USD" && (
              <span style={{
                marginLeft: "auto", fontSize: "0.62rem", fontWeight: 700,
                background: "rgba(96,165,250,0.15)", border: "1px solid rgba(96,165,250,0.3)",
                borderRadius: 4, padding: "1px 7px", color: "#60a5fa", letterSpacing: "0.3px",
              }}>Banco USD</span>
            )}
          </div>

          {/* Cuerpo */}
          <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
            {/* TC row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.73rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                TC compra:
              </span>

              {loadingTC
                ? <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#60a5fa", fontSize: "0.7rem" }} />
                : <span style={{
                    fontSize: "0.64rem", fontWeight: 700,
                    background: `${badge.color}18`, border: `1px solid ${badge.color}45`,
                    borderRadius: 4, padding: "2px 7px", color: badge.color, letterSpacing: "0.3px",
                  }}>{badge.text}</span>
              }

              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                background: "rgba(0,0,0,0.2)", border: "1px solid var(--border-card)",
                borderRadius: 7, padding: "4px 10px 4px 9px",
              }}>
                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontWeight: 600, lineHeight: 1 }}>₡</span>
                <input
                  type="number" step="0.01" min="0.01"
                  value={receiptData.tipo_cambio}
                  onChange={(e) => updateField("tipo_cambio", parseFloat(e.target.value) || 1)}
                  style={{
                    width: 82, background: "transparent", border: "none",
                    color: "var(--text-primary)", fontSize: "0.88rem",
                    fontFamily: "monospace", fontWeight: 600, outline: "none", padding: 0,
                  }}
                />
                <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>/ USD</span>
              </div>

              {bancoMoneda === "CRC" && (
                <span style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                  (referencia para facturas USD)
                </span>
              )}
            </div>

            {/* Equivalencia — dentro del card */}
            {receiptData.monto > 0 && (() => {
              const tc = Number(receiptData.tipo_cambio) || 1;
              const m  = Number(receiptData.monto);
              return (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                  background: "rgba(0,0,0,0.15)", borderRadius: 7, padding: "8px 12px",
                }}>
                  <i className="fa-solid fa-arrow-right-arrow-left"
                     style={{ color: "var(--text-muted)", fontSize: "0.62rem", flexShrink: 0 }} />
                  {bancoMoneda === "USD" ? (
                    <>
                      <span style={{ color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                        ${formatCurrency(m)}
                      </span>
                      <span style={{ color: "var(--text-muted)", fontSize: "0.74rem" }}>USD equivale a</span>
                      <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                        ₡{formatCurrency(m * tc)}
                      </span>
                      <span style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>al TC {tc.toFixed(2)}</span>
                    </>
                  ) : (
                    <>
                      <span style={{ color: "var(--text-secondary)", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                        ₡{formatCurrency(m)}
                      </span>
                      <span style={{ color: "var(--text-muted)", fontSize: "0.74rem" }}>equivale a</span>
                      <span style={{ color: "#60a5fa", fontFamily: "monospace", fontWeight: 700, fontSize: "0.88rem" }}>
                        ${(m / tc).toFixed(4)} USD
                      </span>
                      <span style={{ color: "var(--text-muted)", fontSize: "0.7rem" }}>al TC {tc.toFixed(2)}</span>
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Fila 3: Monto ─────────────────────────────────────────── */}
      <div className="row" style={{ marginTop: 8 }}>
        <div className="form-group">
          <label className="form-label">
            {monedaSimbolo} {t("invoices.montoTotal")}{" "}
            <span style={{ color: "#f87171" }}>*</span>
            {multimoneda && bancoMoneda === "USD" && (
              <span style={{ fontWeight: 400, fontSize: "0.78rem", color: "#60a5fa", marginLeft: 6 }}>
                (en dólares)
              </span>
            )}
          </label>
          <div className="d-flex" style={{ flexDirection: "column" }}>
            <div className="d-flex">
              <div style={{
                display: "flex", alignItems: "center",
                background: "var(--bg-input)", border: "1px solid var(--border-card)",
                borderRadius: "7px 0 0 7px", padding: "0 10px",
                color: bancoMoneda === "USD" ? "#60a5fa" : "var(--text-secondary)",
                fontWeight: 700, fontSize: "0.9rem",
              }}>
                {monedaSimbolo}
              </div>
              <input
                className="form-control"
                type="text"
                value={displayValue}
                placeholder="0,00"
                disabled={readOnly}
                style={{ ...errorBorder("monto"), borderRadius: "0", borderLeft: "none" }}
                onFocus={() => {
                  if (readOnly) return;
                  setDisplayValue(receiptData.monto ? receiptData.monto.toString().replace(".", ",") : "");
                }}
                onChange={(e) => {
                  if (readOnly) return;
                  let raw = e.target.value.replace(/[^0-9,]/g, "");
                  const parts = raw.split(",");
                  if (parts.length > 2) return;
                  if (parts[1]?.length > 2) return;
                  const num = Number(raw.replace(",", ".")) || 0;
                  setDisplayValue(raw);
                  updateField("monto", Math.min(num, 999999999999999));
                }}
                onBlur={() => {
                  if (readOnly) return;
                  setDisplayValue(receiptData.monto > 0 ? formatCurrency(receiptData.monto) : "");
                }}
              />
              <button className="btn btn-primary" onClick={onDistributeAllMoney}>
                {t("invoices.distribuir")}
              </button>
            </div>
            <FieldError msg={errors.monto} />
          </div>
        </div>
      </div>

      {/* ── Fila 4: Referencia bancaria ───────────────────────────── */}
      <div className="row">
        <div className="form-group">
          <label className="form-label">
            {t("invoices.referenciaBancaria")}{" "}
            {soloSaldoFavor
              ? <span style={{ color: "#f97316", fontSize: "0.78rem", fontWeight: 400 }}>({t("invoices.referenciaAutoSaldo")})</span>
              : <span style={{ color: "#f87171" }}>*</span>}
          </label>
          <input
            className="form-control"
            type="text"
            value={soloSaldoFavor ? (referenciaAutoSaldo || t("invoices.refSaldoFavorPlaceholder")) : receiptData.documento}
            onChange={(e) => { if (!soloSaldoFavor) updateField("documento", e.target.value); }}
            disabled={readOnly || soloSaldoFavor}
            placeholder={soloSaldoFavor ? t("invoices.refSaldoFavorPlaceholder") : t("invoices.referenciaPlaceholder")}
            style={{
              ...errorBorder("documento"),
              ...(soloSaldoFavor ? { background: "rgba(249,115,22,0.06)", borderColor: "rgba(249,115,22,0.3)", color: "#f97316", cursor: "not-allowed" } : {}),
            }}
          />
          {soloSaldoFavor && (
            <div style={{ fontSize: "0.76rem", color: "#f97316", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
              <i className="fa-solid fa-circle-info" />
              {t("invoices.referenciaHint")}
            </div>
          )}
          <FieldError msg={errors.documento} />
        </div>
      </div>

      {/* ── Fila 5: Descripción (anexo bancario) ─────────────────── */}
      <div className="row">
        <div className="form-group">
          <label className="form-label">
            {t("invoices.descripcionOpcional")}{" "}
            <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
              ({t("invoices.descripcionOpcionalSufijo")})
            </span>
          </label>
          <input
            className="form-control"
            type="text"
            value={receiptData.descripcion ?? ""}
            onChange={(e) => updateField("descripcion", e.target.value)}
            disabled={readOnly}
            placeholder={t("invoices.descripcionPlaceholder")}
          />
          <div style={{ fontSize: "0.73rem", color: "var(--text-muted)", marginTop: 3 }}>
            {t("invoices.descripcionHint")}
          </div>
        </div>
      </div>
    </div>
  );
}
